import { computed, observable, type IComputedValue, type IObservableValue } from 'mobx';

import {
  getNodeInstance,
  getNodeInstanceTreeNode,
  type NodeInstanceSummary,
  type NodeInstanceTreeNode,
} from 'api/node_instances/operations/getNodeInstanceTree';
import { extractErrorMessage } from 'helpers/extractErrorMessage';

/**
 * Synthetic id for the placeholder shown above a broken (deleted) ancestor.
 * Safe as a sentinel because real node instance ids are positive serials — NAP
 * never assigns -1 — so it can't collide with a genuine node in the tree index.
 */
export const MISSING_ANCESTOR_ID = -1;

/** A node in the navigator tree. `summary` is null only for the missing-ancestor placeholder. */
export interface InstanceTreeNode {
  readonly id: number;
  readonly summary: NodeInstanceSummary | null;
  readonly children: readonly InstanceTreeNode[];
  readonly hasChildren: boolean;
}

const toTreeNode = (node: NodeInstanceTreeNode): InstanceTreeNode => ({
  id: node.nodeInstanceId,
  summary: node,
  children: node.children.map(toTreeNode),
  hasChildren: node.hasChildNodes || node.children.length > 0,
});

interface TreeIndex {
  readonly byId: ReadonlyMap<number, InstanceTreeNode>;
  readonly parentById: ReadonlyMap<number, number | null>;
}

const buildIndex = (root: InstanceTreeNode | null): TreeIndex => {
  const byId = new Map<number, InstanceTreeNode>();
  const parentById = new Map<number, number | null>();
  const walk = (node: InstanceTreeNode, parent: number | null) => {
    byId.set(node.id, node);
    parentById.set(node.id, parent);
    node.children.forEach((child) => walk(child, node.id));
  };
  if (root) walk(root, null);
  return { byId, parentById };
};

const matches = (node: InstanceTreeNode, term: string): boolean =>
  node.summary != null && (node.summary.name.toLowerCase().includes(term) || String(node.id).includes(term));

interface FilteredTree {
  readonly roots: readonly InstanceTreeNode[];
  readonly expandIds: ReadonlySet<number>;
  readonly matchCount: number;
}

/**
 * Prunes the tree to matches + their ancestors and collects the branches to
 * force-expand so every match stays on screen. With no filter, returns the
 * tree unchanged and an empty force-expand set (the model uses the user's own
 * expansion then).
 */
const filterTree = (root: InstanceTreeNode | null, filter: string): FilteredTree => {
  const term = filter.trim().toLowerCase();
  if (!root) return { roots: [], expandIds: new Set(), matchCount: 0 };
  if (term === '') return { roots: [root], expandIds: new Set(), matchCount: 0 };

  const expandIds = new Set<number>();
  let matchCount = 0;
  const prune = (node: InstanceTreeNode): InstanceTreeNode | null => {
    const children = node.children.map(prune).filter((child): child is InstanceTreeNode => child !== null);
    const self = matches(node, term);
    if (self) matchCount += 1;
    if (!self && children.length === 0) return null;
    if (children.length > 0) expandIds.add(node.id);
    return { ...node, children };
  };
  const prunedRoot = prune(root);
  return {
    roots: prunedRoot ? [prunedRoot] : [],
    expandIds,
    matchCount,
  };
};

interface ClimbResult {
  readonly rootId: number;
  /** The id of the missing parent when the chain is broken; null otherwise. */
  readonly missingParentId: number | null;
}

/**
 * Modal-scoped state for the ancestor + siblings tree navigator. Owns the
 * loaded tree, the active (shown) instance, expansion, and the filter, plus the
 * async load that walks the parent chain to the root and fetches the subtree.
 * The component is a pure observer; all logic here is unit-testable without
 * React.
 */
export class InstanceTreeModel {
  private readonly _activeId: IObservableValue<number>;
  private readonly _expandedIds = observable.box<ReadonlySet<number>>(new Set(), { deep: false });
  private readonly _filter = observable.box('');
  private readonly _root = observable.box<InstanceTreeNode | null>(null, {
    deep: false,
  });
  private readonly _loading = observable.box(false);
  private readonly _error: IObservableValue<string | null> = observable.box<string | null>(null);
  private readonly _index: IComputedValue<TreeIndex>;
  private readonly _filtered: IComputedValue<FilteredTree>;

  /** Resolves once the initial load settles (success or failure). For tests. */
  readonly loaded: Promise<void>;

  constructor(entryId: number) {
    this._activeId = observable.box(entryId);
    this._index = computed(() => buildIndex(this._root.get()));
    this._filtered = computed(() => filterTree(this._root.get(), this._filter.get()));
    this.loaded = this.load(entryId);
  }

  get activeId(): number {
    return this._activeId.get();
  }

  get error(): string | null {
    return this._error.get();
  }

  /** Whether a filter is currently narrowing the tree. */
  get filtering(): boolean {
    return this._filter.get().trim() !== '';
  }

  get filter(): string {
    return this._filter.get();
  }

  /** The expansion set to render: forced match-paths while filtering, else the user's. */
  get expandedIds(): ReadonlySet<number> {
    return this.filtering ? this._filtered.get().expandIds : this._expandedIds.get();
  }

  get loading(): boolean {
    return this._loading.get();
  }

  get matchCount(): number {
    return this._filtered.get().matchCount;
  }

  /** Whether the tree has loaded a root yet. */
  get ready(): boolean {
    return this._root.get() !== null;
  }

  get roots(): readonly InstanceTreeNode[] {
    return this._filtered.get().roots;
  }

  setActive = (id: number): void => {
    this._activeId.set(id);
    this.expandPathTo(id);
  };

  setFilter = (value: string): void => {
    this._filter.set(value);
  };

  toggleExpand = (id: number): void => {
    const next = new Set(this._expandedIds.get());
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this._expandedIds.set(next);
  };

  /**
   * Activates `id` if it is part of the loaded tree (returns true), otherwise
   * returns false so the caller can route out (e.g. open a new tab). This is
   * how an out-of-tree link — an originating node in a different flow — falls
   * through to its own fresh tree.
   */
  tryActivate(id: number): boolean {
    if (!this._index.get().byId.has(id)) return false;
    this.setActive(id);
    return true;
  }

  private expandPathTo(id: number): void {
    const { parentById } = this._index.get();
    const next = new Set(this._expandedIds.get());
    let current: number | null | undefined = id;
    while (current != null) {
      next.add(current);
      current = parentById.get(current) ?? null;
    }
    this._expandedIds.set(next);
  }

  private async climbToRoot(entryId: number): Promise<ClimbResult> {
    const entry = await getNodeInstance(entryId);
    if (!entry) {
      throw new Error(`Node instance ${entryId} not found`);
    }
    let current = entry;
    // A cyclic/self-referential parent_node_instance_id is a DB-integrity
    // violation that shouldn't happen, but would otherwise hang the tab in an
    // infinite climb hammering the API. Stop the moment we'd revisit a node:
    // root at the highest node reached, no missing-ancestor gap.
    const visited = new Set<number>([current.nodeInstanceId]);
    while (current.parentNodeInstanceId != null) {
      if (visited.has(current.parentNodeInstanceId)) {
        return { rootId: current.nodeInstanceId, missingParentId: null };
      }
      const parent = await getNodeInstance(current.parentNodeInstanceId);
      if (!parent) {
        // A deleted intermediate breaks the chain: root at the highest
        // reachable node and remember the gap for a placeholder.
        return {
          rootId: current.nodeInstanceId,
          missingParentId: current.parentNodeInstanceId,
        };
      }
      visited.add(parent.nodeInstanceId);
      current = parent;
    }
    return { rootId: current.nodeInstanceId, missingParentId: null };
  }

  private async load(entryId: number): Promise<void> {
    this._loading.set(true);
    this._error.set(null);
    try {
      const { rootId, missingParentId } = await this.climbToRoot(entryId);
      let root = toTreeNode(await getNodeInstanceTreeNode(rootId));
      if (missingParentId != null) {
        root = {
          id: MISSING_ANCESTOR_ID,
          summary: null,
          children: [root],
          hasChildren: true,
        };
      }
      this._root.set(root);
      this.expandPathTo(entryId);
    } catch (err) {
      this._error.set(extractErrorMessage(err, 'Failed to load the instance tree'));
    } finally {
      this._loading.set(false);
    }
  }
}
